/**
 * Floating assistant chat widget — a launcher bubble pinned to the bottom
 * right that opens a chat panel for the assistant of the page on screen: the
 * Network Assistant, or the Frontdoor Assistant in the FD sub-app.
 *
 * Rendered globally from the app template; hides itself until a scope is
 * resolved *and* the assistant service reports the assistant switched on for
 * it. Conversation state lives in AssistantChatService so it survives route
 * navigation, and in sessionStorage so it survives reloads.
 */

import {
    AfterViewChecked,
    Component,
    ElementRef,
    OnDestroy,
    ViewChild,
    computed,
    effect,
    untracked,
} from '@angular/core';
import { AssistantChatService, AssistantChatMessage } from './assistant-chat.service';
import { AiConfigService } from './api/assistant-config.service';
import { AssistantMessageSegment, segmentAssistantMessage } from './assistant-message-segments.util';
import { QuestionReply } from './assistant-question.component';
import { withoutOptionList } from './assistant-question.util';

@Component({
    selector: 'app-assistant-chat',
    templateUrl: './assistant-chat.component.html',
    styleUrls: ['./assistant-chat.component.scss'],
    standalone: false,
})
export class AssistantChatComponent implements AfterViewChecked, OnDestroy {
    /**
     * Set on <body> while the launcher is up. The bottom-right corner is the
     * HubSpot messages widget's the rest of the time, and that widget lives
     * outside the Angular tree, so the app stylesheet slides it left off this
     * class (see overrides.scss). Dropping the class puts it back.
     */
    static readonly BODY_CLASS = 'assistant-launcher-visible';

    @ViewChild('messageList') private messageList?: ElementRef<HTMLDivElement>;
    @ViewChild('chatInput') private chatInput?: ElementRef<HTMLTextAreaElement>;

    draft = '';

    /** Shown only while the assistant can take a question - see AssistantChatService.available. */
    readonly visible = computed(() => this.chat.available());

    /** Pin the transcript to the bottom while messages stream in. */
    private shouldScroll = false;

    /**
     * Shell-style history recall. Index into `chat.sentHistory` while
     * browsing with arrow-up/down, or null when not browsing. Whatever was in
     * the input when browsing started is stashed and restored on arrow-down
     * past the newest entry.
     */
    private historyIndex: number | null = null;
    private stashedDraft = '';

    constructor(
        public chat: AssistantChatService,
        private aiConfig: AiConfigService
    ) {
        // Ask once per scope. Any later read or write of the config refreshes
        // the same cached flag, so switching the assistant off hides this widget
        // without a reload.
        effect(() => {
            const scope = this.chat.scope();
            if (!scope) {
                return;
            }
            untracked(() => {
                if (this.aiConfig.enabledFor(scope) !== null) {
                    return;
                }
                this.aiConfig.get(scope).subscribe({
                    // A scope whose assistant config can't be read has no working
                    // assistant to launch; `visible` already treats unknown as hidden.
                    // Say so once, though: 404 means the assistant isn't deployed or
                    // routed in this environment.
                    error: (err) =>
                        console.warn(
                            `[assistant] hiding the assistant launcher — could not read the assistant config for ${scope.kind}/${scope.id}` +
                                ` (${err?.status ?? 'network error'})`,
                            err
                        ),
                });
            });
        });

        // Claim the corner only while the launcher is actually up.
        effect(() => {
            document.body.classList.toggle(AssistantChatComponent.BODY_CLASS, this.visible());
        });
    }

    /** Logout tears this widget down — give the corner back on the way out. */
    ngOnDestroy(): void {
        document.body.classList.remove(AssistantChatComponent.BODY_CLASS);
    }

    ngAfterViewChecked(): void {
        // While a reply streams in, every change-detection pass re-pins the
        // transcript; otherwise only explicit actions (open, send) scroll.
        if ((this.shouldScroll || this.chat.isStreaming()) && this.messageList) {
            const el = this.messageList.nativeElement;
            el.scrollTop = el.scrollHeight;
            this.shouldScroll = false;
        }
    }

    onToggle(): void {
        this.chat.toggleOpen();
        if (this.chat.isOpen()) {
            this.shouldScroll = true;
            // Focus after the panel renders.
            setTimeout(() => this.chatInput?.nativeElement.focus());
        }
    }

    onSend(): void {
        const text = this.draft.trim();
        if (!text || this.chat.isStreaming()) {
            return;
        }
        this.draft = '';
        this.historyIndex = null;
        this.stashedDraft = '';
        this.shouldScroll = true;
        // The input was cleared programmatically — shrink it back to one row.
        setTimeout(() => this.autoGrow());
        void this.chat.send(text).then(() => (this.shouldScroll = true));
    }

    /**
     * Send an answer to the latest reply's question as the next message. The draft is left alone, so a half-typed reply
     * is not lost.
     */
    onAnswered(reply: QuestionReply): void {
        if (this.chat.isStreaming()) {
            return;
        }
        this.shouldScroll = true;
        void this.chat.send(reply.text, reply.answer).then(() => (this.shouldScroll = true));
    }

    /**
     * The suggested answers are offered only on the latest reply, once it has finished. A later message
     * answers the question, so an older one's chips would only send a stale answer.
     */
    answerable(message: AssistantChatMessage, last: boolean): boolean {
        const answers = (message.question?.options.length ?? 0) + (message.question?.input ? 1 : 0);
        return last && !message.pending && !message.error && !this.chat.isStreaming() && answers > 0;
    }

    onInputKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.onSend();
            return;
        }
        if (event.key === 'ArrowUp') {
            this.recallOlder(event);
        } else if (event.key === 'ArrowDown') {
            this.recallNewer(event);
        }
    }

    /** Typing exits history-browsing mode; the edited text becomes the draft. */
    onDraftChanged(): void {
        this.historyIndex = null;
        this.autoGrow();
    }

    /**
     * Grow the input to fit its content so multi-line drafts stay fully
     * visible. Resetting to `auto` first lets it shrink back when text is
     * removed; CSS `max-height` caps the growth and switches to scrolling.
     */
    private autoGrow(): void {
        const el = this.chatInput?.nativeElement;
        if (!el) {
            return;
        }
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }

    private recallOlder(event: KeyboardEvent): void {
        const history = this.chat.sentHistory;
        if (history.length === 0) {
            return;
        }
        // Only hijack arrow-up when it can't mean caret movement: the input is
        // empty, or we're already browsing history.
        if (this.historyIndex === null && this.draft !== '') {
            return;
        }

        if (this.historyIndex === null) {
            this.stashedDraft = this.draft;
            this.historyIndex = history.length - 1;
        } else if (this.historyIndex > 0) {
            this.historyIndex--;
        }
        event.preventDefault();
        this.setDraftFromHistory(history[this.historyIndex]);
    }

    private recallNewer(event: KeyboardEvent): void {
        if (this.historyIndex === null) {
            return;
        }
        const history = this.chat.sentHistory;

        event.preventDefault();
        if (this.historyIndex < history.length - 1) {
            this.historyIndex++;
            this.setDraftFromHistory(history[this.historyIndex]);
        } else {
            // Past the newest entry — back to whatever was being typed.
            this.historyIndex = null;
            this.setDraftFromHistory(this.stashedDraft);
            this.stashedDraft = '';
        }
    }

    private setDraftFromHistory(text: string): void {
        this.draft = text;
        // Place the caret at the end once ngModel has written the value.
        setTimeout(() => {
            const el = this.chatInput?.nativeElement;
            if (el) {
                el.selectionStart = el.selectionEnd = el.value.length;
            }
            // Recalled text may span multiple lines — fit the input to it.
            this.autoGrow();
        });
    }

    onToggleExpanded(): void {
        this.chat.toggleExpanded();
        // The transcript height changes — keep it pinned to the latest message.
        this.shouldScroll = true;
    }

    onNewConversation(): void {
        this.chat.newConversation();
        this.chatInput?.nativeElement.focus();
    }

    /**
     * Segments memoized per message object and chip state. The chat service replaces a message on every update, so
     * identity is the cache key, and a finished message is not parsed again on later change-detection passes.
     */
    private readonly segmentCache = new WeakMap<
        AssistantChatMessage,
        { chips: boolean; segments: AssistantMessageSegment[] }
    >();

    /**
     * The message rendered as alternating markdown and chart segments — charts
     * sit inline where their `[chart:<id>]` marker appears in the text, and
     * after it when unreferenced. A reply that shows its question's option chips drops the numbered list they repeat.
     */
    segments(message: AssistantChatMessage, last: boolean): AssistantMessageSegment[] {
        const chips = this.answerable(message, last) && !message.question?.input;
        const cached = this.segmentCache.get(message);
        if (cached?.chips === chips) {
            return cached.segments;
        }
        const text = chips ? withoutOptionList(message.text, message.question) : message.text;
        const segments = segmentAssistantMessage(text, message.charts, message.tables);
        this.segmentCache.set(message, { chips, segments });
        return segments;
    }
}
